<template>
  <div class="relative">
    <div
      ref="paypalContainerRef"
      id="paypal-container"
      class="h-12 overflow-hidden"
      :class="{ 'adyen-paypal first:flex first:justify-center text-nowrap': showPayPalPayLater && style!.layout === 'horizontal' }"
      style="contain: layout style"
    />
    <vf-button v-if="!paypalButtonRendered" class="top-0 h-12 w-full !absolute" loading variant="tertiary" />
    <lazy-paypal-payments v-if="showPayPalPayLater" :amount="cart.totals.total" class="mt-4" placement="payment" />
  </div>
</template>

<script lang="ts" setup>
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { InvoiceData, ShippingAddress } from '#types/cart'
import type { PaypalAddress, PaypalConfiguration } from '#types/paypal'

export type Props = {
  billingAddress?: ShippingAddress
  instance?: any
  context?: string
  hidePayLaterText?: boolean
  invoiceAddress?: InvoiceData
  isExpressFlow?: boolean
  isSameAsShipping?: boolean
  locale?: string
  selectedVariant?: ProductVariants[number]
  beforeProceed?: (context?: 'paypal' | 'address_bypass') => Promise<boolean>
}

const {
  beforeProceed,
  billingAddress,
  context,
  hidePayLaterText,
  invoiceAddress,
  isExpressFlow,
  isSameAsShipping,
  locale = '',
  selectedVariant,
  instance
} = defineProps<Props>()

const emit = defineEmits<{
  reject: []
}>()

const { style } = useAppConfig().components.paypal.button
const { paypal } = useAppConfig().checkout
const cart = useCartStore(context)
const { basketId } = storeToRefs(cart)
const { getApplicableShippingMethods } = cart
const cartApi = useApi().cart
const recaptcha = useRecaptcha()
const checkout = useCheckoutStore(context)
const { billingAddressRef } = storeToRefs(checkout)
const paypalPaymentData = ref()
const paymentInstrumentId = ref()
const isPaypalAddressComplete = ref(false)
const router = useRouter()
const { $feature } = useNuxtApp()
const showPayPalPayLater = computed(() => $feature.showPayPalPayLater && !hidePayLaterText)
const paypalButtonLoaded = computed(() => !!instance)

const paypalContainerRef = ref()

const paypalButtonRendered = ref(false)

const addBillingAddress = async () => {
  if (!isExpressFlow) {
    let address

    if (isSameAsShipping) {
      address = {
        ...invoiceAddress,
        firstName: checkout.shippingAddress?.firstName || '',
        lastName: checkout.shippingAddress?.lastName || '',
        country: checkout.shippingAddress?.country || '',
        addressLine1: checkout.shippingAddress?.addressLine1 || '',
        postalCode: checkout.shippingAddress?.postalCode || '',
        city: checkout.shippingAddress?.city || '',
        phone: checkout.shippingAddress?.phone || '',
        email: checkout.shippingAddress?.email || '',
        addressId: checkout.shippingAddress?.addressId || '',
        subscription: checkout.shippingAddress?.subscriptions || {},
        province: checkout.shippingAddress?.province || ''
      }
    }
    else {
      address = { ...billingAddress, ...invoiceAddress }
    }

    if (address)
      await cart.addBillingAddress(address)
  }
}

const onSubmit = async (state, component) => {
  if (context === 'paypalPdp' && selectedVariant) {
    await cart.add({
      product: selectedVariant.id,
      upc: selectedVariant.upc,
      suppressMiniCart: true,
      suppressNotification: true,
      maxQty: 1
    })
  }
  const response = await cart.postPaymentInstruments('PAYPAL', { additionalDetails: { paymentData: JSON.stringify(state.data) } })
  const paymentInstrument = response.payment_instruments.find(({ payment_method_id }) => payment_method_id === 'PAYPAL')
  component.handleAction(JSON.parse(paymentInstrument.c_requestID))
  paymentInstrumentId.value = paymentInstrument.payment_instrument_id
  return response
}

const isShippingAddressIncomplete = (address) => {
  const fieldsToSkip = [
    'id',
    'addressId',
    'addressLine2',
    'addressLine3',
    'stateCode',
    'subscriptions',
  ]
  return Object.keys(address).some((field) => {
    if (fieldsToSkip.includes(field)) return false
    return !address[field]
  })
}

const mapAddressData = (address: PaypalAddress) => {
  const [addressLine1, addressLine2] = address.street?.split(',') || []
  const stateCode = address.state?.toUpperCase() || address.stateOrProvince?.toUpperCase() || ''

  return {
    addressId: '',
    firstName: address.given_name || '',
    lastName: address.surname || '',
    email: address.shopperEmail || '',
    country: address.countryCode?.toUpperCase() || address.country?.toUpperCase() || '',
    addressLine1: addressLine1 || '',
    addressLine2: addressLine2 || '',
    postalCode: address.postalCode || '',
    city: address.city || '',
    province: stateCode,
    phone: address.telephoneNumber || '',
    stateCode,
    countryCode: address.countryCode?.toUpperCase() || address.country?.toUpperCase() || '',
    region: stateCode
  }
}

let paypalConfiguration: PaypalConfiguration = {
  style,
  countryCode: useCountryCode(),
  amount: {
    currency: cart.currency,
    value: cart.totals.total || selectedVariant?.price.current || 0
  },
  ...paypal[useLocale()].config,
  blockPayPalCreditButton: true,
  blockPayPalPayLaterButton: !showPayPalPayLater.value,
  blockPayPalVenmoButton: true,
  showPayButton: true,
  isExpress: isExpressFlow,
  onInit: () => {
    paypalButtonRendered.value = true
  },
  onClick: async (data, actions) => {
    if (typeof beforeProceed === 'function') {
      const ok = await beforeProceed('paypal')
      if (!ok) {
        emit('reject')
        // Reject the action to prevent PayPal flow from starting
        return actions.reject()
      }
    }
    // If everything is okay, resolve to continue
    return actions.resolve()
  },
  locale // This is needed for translating PayPal button labels it has to be in en_US format
}

if (!isExpressFlow) {
  paypalConfiguration = {
    ...paypalConfiguration,
    onSubmit: async (state, component) => {
      await addBillingAddress()
      const response = await onSubmit(state, component)
      const stateCode = response.billingAddress?.province || ''
      billingAddressRef.value = {
        ...response.billingAddress,
        invoiceRequested: invoiceAddress?.invoiceRequested,
        stateCode,
        region: stateCode
      }
    },
    onAdditionalDetails: async (state) => {
      await checkout.prepareOrder()

      checkout.placeOrder({
        code: 'PAYPAL',
        id: 'adyen',
        additionalData: [
          {
            c_paymentID: '',
            c_payerID: JSON.stringify(state.data),
            c_requestID: '',
            payment_instrument_id: paymentInstrumentId.value,
            payment_method_id: 'PAYPAL'
          }
        ]
      }, recaptcha)
    }
  }
}
else {
  paypalConfiguration = {
    ...paypalConfiguration,
    onAuthorized: async (shopperDetails, actions) => {
      const { authorizedEvent, billingAddress, deliveryAddress } = shopperDetails
      const {
        payer: {
          email_address: shopperEmail,
          name: shopperName,
          phone: {
            phone_number: {
              national_number: telephoneNumber
            }
          }
        }
      } = authorizedEvent

      // Create Shipping Object from paypal
      const createShippingObj = {
        ...deliveryAddress,
        shopperEmail,
        ...shopperName,
        telephoneNumber
      }

      const paypalShippingAddress = mapAddressData(createShippingObj)
      isPaypalAddressComplete.value = !isShippingAddressIncomplete(paypalShippingAddress)
      try {
        await getApplicableShippingMethods()
        // Set Shipping Address
        const captchaResponse = $feature.enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        const response = await cartApi.$addShippingAddress(basketId.value, {
          ...paypalShippingAddress,
          subscriptions: {
            newsletterConsent: false
          },
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { context: 'PAYPAL_EXPRESS', ...context === 'paypalPdp' ? { bid: 'cookie' } : {} })
        // Set payment data
        const paymentInstrument = response.payment_instruments.find((payment_instrument) => payment_instrument.payment_method_id === 'PAYPAL')
        const c_requestID = JSON.parse(paymentInstrument.c_requestID)
        paypalPaymentData.value = c_requestID
      }
      catch (e) {
        console.log('Error adding shipping address', e)
      }

      // Create Billing Object from paypal
      const createBillingObj = {
        ...billingAddress,
        shopperEmail,
        ...shopperName,
        telephoneNumber
      }
      const newBillingAddress = mapAddressData(createBillingObj)

      billingAddressRef.value = { ...newBillingAddress }

      actions.resolve()
    },
    onShippingAddressChange: async (data, _actions, component) => {
      const captchaResponse = $feature.enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
      const response = await cartApi.$addShippingAddress(basketId.value, {
        ...mapAddressData(data.shippingAddress),
        subscriptions: {
          newsletterConsent: false
        },
        ...captchaResponse && { recaptcha_response: captchaResponse }
      }, { context: 'PAYPAL_EXPRESS', ...context === 'paypalPdp' ? { bid: 'cookie' } : {} })

      const paymentInstrument = response.payment_instruments.find((payment_instrument) => payment_instrument.payment_method_id === 'PAYPAL')
      const c_requestID = JSON.parse(paymentInstrument.c_requestID)
      component.updatePaymentData(c_requestID.paymentData)
    },
    onSubmit,
    onAdditionalDetails: async (state, component) => {
      if (isPaypalAddressComplete.value) {
        // Update paypals payment data
        component.updatePaymentData(paypalPaymentData.value)

        if (context !== 'paypalPdp') await checkout.prepareOrder()

        checkout.placeOrder({
          code: 'PAYPAL',
          id: 'adyen',
          billingAddress: billingAddressRef.value,
          additionalData: [
            {
              c_paymentID: '',
              c_payerID: JSON.stringify(state.data),
              c_requestID: '',
              payment_instrument_id: paymentInstrumentId.value,
              payment_method_id: 'PAYPAL'
            }
          ]
        }, recaptcha)
      }
      else {
        // If the address is incomplete navigate the user to shipping page
        router.push(useLocalisedRoute('/checkout'))
      }
    }
  }
}

onMounted(() => {
  whenever(paypalButtonLoaded, () => {
    nextTick(() => new window.AdyenWeb.PayPal(instance, paypalConfiguration).mount(paypalContainerRef.value))
  }, { immediate: true })
})
</script>
